import React, {useEffect, useRef} from 'react';
import * as R from 'ramda';
import {useTranslation} from 'react-i18next';
import {mapWithIndex} from '@/util';
import {select as d3$} from 'd3';
import {donut} from 'britecharts';

import {codeMapping} from '@/config';

const paint = (data, colorMap, target) => {
  const container = d3$(target);

  container.select('svg').remove();

  if (!data.length) {
    return;
  }

  container.datum(data);

  const donutChart = donut()
    .width(370)
    .height(370)
    .colorSchema(colorMap)
    .centeredTextFunction(
      ({percentage, displayName}) => `${percentage} ${displayName}`
    )
    .externalRadius(150)
    .internalRadius(90)
    .orderingFunction((a, b) => a.id - b.id);

  container.call(donutChart);
  container
    .select('.legend-group')
    .insert('circle', '.donut-text')
    .attr('r', 100)
    .style('fill', '#000')
    .style('opacity', '.1');
  container
    .select('.legend-group')
    .insert('circle', '.donut-text')
    .attr('r', 90)
    .style('fill', '#fff');
};

const IconItem = ({cssClass, code}) => <li className={cssClass}>{code}</li>;

const Item = ({item: {code, name, assetClass, percentage}}) => (
  <tr>
    <td>{code}</td>
    <td>{name}</td>
    <td>{assetClass || '-'}</td>
    <td>{percentage}%</td>
  </tr>
);

const BreakDownChart = ({type, data, subTitle}) => {
  const {t: tr} = useTranslation('statements');
  // FIXME: need more color at least 12
  const cssClass = ['a', 'b', 'c', 'd', 'e', 'f', 'a', 'b', 'c', 'd', 'e', 'f'];
  const categoryColorMap = codeMapping('plan_record_type_color');

  const iconList = R.addIndex(R.map)((item, idx) => (
    <IconItem cssClass={cssClass[idx]} code={item.code} key={item.code} />
  ))(data);

  const ItemList = R.map(item => <Item item={item} key={item.code} />)(data);

  const itemListRwd = columnTitle =>
    mapWithIndex(({name, assetClass, percentage}, idx) => (
      <li key={idx}>
        <strong>{name}</strong>
        <table>
          <tbody>
            <tr>
              <td>
                {tr('asset_class')} / {columnTitle}
              </td>
              <td>
                {assetClass || '-'} / {percentage}%
              </td>
            </tr>
          </tbody>
        </table>
      </li>
    ));

  const chartData = R.addIndex(R.map)((item, idx) => ({
    id: idx + 1,
    name: item.code,
    displayName: item.name,
    quantity: item.percentage,
    percentage: `${item.percentage}%`,
  }))(data);

  const canvas = useRef(null);

  useEffect(
    () => {
      paint(chartData, R.values(categoryColorMap), canvas.current);
    },
    [data]
  );

  const labelMapping = {
    allocation: {
      sectionTitle: tr('contribution_allocation'),
      fundNameLabel: tr('fund_name'),
      fundCodeLabel: tr('fund_code'),
      percentageLabel: tr('allocation_percentage'),
    },
    portfolio: {
      sectionTitle: tr('portfolio_breakdown'),
      fundNameLabel: tr('fund_name'),
      fundCodeLabel: tr('fund_code'),
      percentageLabel: tr('portfolio_percentage'),
    },
    abfFund: {
      sectionTitle: tr('abf_fund_descriptions'),
      fundNameLabel: tr('abf_fund_allocation'),
      fundCodeLabel: '',
      percentageLabel: tr('portfolio_percentage'),
    },
    etfFund: {
      sectionTitle: tr('etf_fund_descriptions'),
      fundNameLabel: tr('etf_fund_allocation'),
      fundCodeLabel: '',
      percentageLabel: tr('portfolio_percentage'),
    },
  };

  const subSectionTitle = R.cond([
    [R.isNil, R.always('')],
    [R.equals('noCoverage'), R.always('')],
    [R.T, () => `(Coverage ${subTitle})`],
  ])(subTitle);

  return (
    <>
      <section className='chart plan-details'>
        <h5>
          {labelMapping[type].sectionTitle} {subSectionTitle}
        </h5>

        {/* wide */}
        <div className='wide row'>
          <div className='col-sm-6'>
            <ul className='clearfix'>{iconList}</ul>
            <div ref={canvas} />
          </div>
          <div className='col-sm-6'>
            <table className='wide list'>
              <tbody>
                <tr>
                  <th>{labelMapping[type].fundCodeLabel}</th>
                  <th>{labelMapping[type].fundNameLabel}</th>
                  <th>{tr('asset_class')}</th>
                  <th>{labelMapping[type].percentageLabel} %</th>
                </tr>
                {ItemList}
              </tbody>
            </table>
          </div>
        </div>

        {/* RWD */}
        <ul className='narrow list'>
          {itemListRwd(labelMapping[type].percentageLabel)(data)}
        </ul>
      </section>
    </>
  );
};

const BreakDownChartList = ({type, data}) =>
  R.compose(
    R.map(([key, val]) => (
      <BreakDownChart key={key} type={type} data={val} subTitle={key} />
    )),
    R.toPairs
  )(data);

export default BreakDownChart;
export {BreakDownChartList};
